import { useCallback, useEffect, useRef } from 'react';

import payload from '@app/payload';
import api from '@services/api';
import pixel from '@services/pixel';
import { getPlanValue } from '@utils/money';
import { phoneToE164 } from '@utils/phone';
import { clearPendingStripeCharge } from '@utils/stripeCheckout';
import { t } from '@i18n';

import type { CheckoutValuesRef } from '../types';

interface UsePaymentCompletionParams {
  valuesRef: CheckoutValuesRef;
  chargeIdRef: { current: any };
  plan: any;
  currency: string;
  isStripe: boolean;
}

export function usePaymentCompletion({
  valuesRef,
  chargeIdRef,
  plan,
  currency,
  isStripe,
}: UsePaymentCompletionParams) {
  const activePolls = useRef(new Set<number>());

  const stopPolling = useCallback((pollId: number) => {
    window.clearInterval(pollId);
    activePolls.current.delete(pollId);
  }, []);

  useEffect(() => () => {
    activePolls.current.forEach((pollId) => window.clearInterval(pollId));
    activePolls.current.clear();
  }, []);

  const handlePaid = useCallback((goTo: string | null = null) => {
    const values = valuesRef.current;
    const orderId = chargeIdRef.current || Date.now().toString();
    const purchaseData = {
      email: values.email,
      name: values.name,
      phone: phoneToE164(values.phone),
      cpf: values.cpf,
      plan,
      value: getPlanValue(plan),
      currency,
      installments: values.installments || 1,
      orderId,
      transactionId: orderId,
      paymentMethod: isStripe ? 'stripe' : values.method,
      success: true,
    };

    pixel.dispatchEvent(pixel.events.purchase, purchaseData);

    if (isStripe) clearPendingStripeCharge();

    const destination = goTo || `https://app.ginastee.com/${payload.slug}`;
    setTimeout(() => {
      window.location.href = destination;
    }, 1700);
  }, [chargeIdRef, currency, isStripe, plan, valuesRef]);

  const watchPixPayment = useCallback((redirect: string | null) => {
    const pollId = window.setInterval(async () => {
      try {
        const response = await api.get(`checkout/charge?charge=${chargeIdRef.current}`);

        if (response.data?.status === 'paid') {
          stopPolling(pollId);
          handlePaid(redirect);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    }, 5000);

    activePolls.current.add(pollId);
  }, [chargeIdRef, handlePaid, stopPolling]);

  const waitForStripePayment = useCallback(() => new Promise((resolve, reject) => {
    const pollId = window.setInterval(async () => {
      try {
        const response = await api.get(`checkout/charge?charge=${chargeIdRef.current}`);
        const status = response.data?.status;

        if (status === 'paid') {
          stopPolling(pollId);
          handlePaid();
          resolve(status);
        } else if (['failed', 'canceled', 'cancelled'].includes(status)) {
          stopPolling(pollId);
          clearPendingStripeCharge();
          reject(new Error(t('payment.error.notApproved')));
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    }, 5000);

    activePolls.current.add(pollId);
  }), [chargeIdRef, handlePaid, stopPolling]);

  return { handlePaid, watchPixPayment, waitForStripePayment };
}

